import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Alert, Button, Stack, TextField } from '@mui/material';

import { formatDateISO } from '~/services/TimeService';
import VendorsService from '~/services/VendorsService';

const SuccessMessage = ({ message }) => {
  return (
    <Alert severity="success">
      <strong>Success!</strong>
      <p>{message}</p>
    </Alert>
  );
};

const ErrorMessage = ({ message }) => {
  return (
    <Alert severity="error">
      <strong>Oops!</strong>
      <p>File upload failed</p>
      <pre>{message}</pre>
    </Alert>
  );
};

export type UploadStatus = 'not-started' | 'completed' | 'failed' | 'pending';

const UploadMusementPayableForm = () => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('not-started');
  const [uploadMessage, setUploadMessage] = useState<string | null>();

  const [file, setFile] = useState<File | null>(null);
  const [paymentDate, setPaymentDate] = useState<string>(formatDateISO(new Date()));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const checkUploadStatus = (jobId: string) => {
    intervalRef.current = setInterval(() => {
      VendorsService.checkUploadJobStatus(jobId)
        .then((result) => {
          const rowsProcessed = result.rows_processed || 0;

          if (result.job_state !== 'completed' && result.job_state !== 'failed') {
            setUploadMessage(`File processing... ${rowsProcessed} rows processed.`);
            return;
          }

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          if (result.status === 'success') {
            const message =
              rowsProcessed > 0
                ? `File uploaded successfully. ${rowsProcessed} row(s) processed.`
                : 'File uploaded successfully but 0 rows processed. Please check file format and/or column names.';

            setUploadStatus('completed');
            setUploadMessage(message);
            return;
          }

          setUploadStatus('failed');
          setUploadMessage(result.result);
        })
        .catch((err) => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setUploadStatus('failed');
          setUploadMessage(err instanceof Error ? err.message : String(err));
        });
    }, 2000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setUploadStatus('pending');

      const formData = new FormData();
      formData.append('musement_payments', file as File);
      formData.append('payment_date', new Date(paymentDate).toISOString());

      const result = await VendorsService.uploadMusementPayments(formData);

      setUploadMessage(result.message);
      checkUploadStatus(result.jobId);
    } catch (e) {
      setUploadStatus('failed');
      setUploadMessage(e instanceof Error ? e.message : String(e));
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files[0]);
  };

  return (
    <form onSubmit={handleSubmit}>
      {uploadStatus === 'completed' && <SuccessMessage message={uploadMessage} />}
      {uploadStatus === 'pending' && uploadMessage && (
        <Alert severity="info">
          <p>{uploadMessage}</p>
        </Alert>
      )}
      {uploadStatus === 'failed' && <ErrorMessage message={uploadMessage} />}

      <Stack direction="column" spacing={2} sx={{ maxWidth: 400 }}>
        <TextField
          label="Date of Payment"
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <input
          type="file"
          onChange={onFileChange}
          accept=".csv, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        />

        <div>
          <Button type="submit" disabled={uploadStatus === 'pending' || !file || !paymentDate} variant="contained">
            {uploadStatus !== 'pending' ? 'Upload' : 'Uploading...'}
          </Button>
        </div>
      </Stack>
    </form>
  );
};

export default UploadMusementPayableForm;
